export const MAX_TTS_CHARS = 2000;

const CLOSE = /[\s"'”’»」』）〉》】)\]}]+$/u;
const ABBREVIATIONS =
  /^(?:mr|mrs|ms|dr|prof|sr|jr|st|vs|etc|approx|dept|fig|inc|ltd|co|no|vol|e\.g|i\.e)\.$/i;
const SENTENCE_END = /[.!?。！？]+["'”’»」』）〉》】)\]}]*\s*/gu;
const CLAUSE_END = /[,;:，；：、]["'”’»」』）〉》】)\]}]*\s*/gu;

function segments(text, locale, granularity) {
  if (typeof Intl === "undefined" || typeof Intl.Segmenter !== "function")
    return null;
  return Array.from(new Intl.Segmenter(locale, { granularity }).segment(text));
}

function safeGraphemeBoundary(text, end) {
  return (
    !/^[\p{M}\p{Emoji_Modifier}\u200D]/u.test(text.slice(end)) &&
    text[end - 1] !== "\u200D"
  );
}

function punctuationBoundaries(text, pattern) {
  const boundaries = [];
  for (const match of text.matchAll(pattern)) {
    const end = match.index + match[0].length;
    if (!safeGraphemeBoundary(text, end)) continue;
    // ASCII punctuation inside decimals or model numbers is not a break.
    if (
      /[。！？，；：、]/u.test(match[0]) ||
      /\s$/u.test(match[0]) ||
      end === text.length
    ) {
      boundaries.push(end);
    }
  }
  return boundaries;
}

function sentenceUnits(text, locale) {
  const native = segments(text, locale, "sentence");
  let pieces;
  if (native) {
    pieces = native.map(({ segment }) => segment);
  } else {
    pieces = [];
    let start = 0;
    for (const end of punctuationBoundaries(text, SENTENCE_END)) {
      pieces.push(text.slice(start, end));
      start = end;
    }
    if (start < text.length) pieces.push(text.slice(start));
  }

  const result = [];
  let pending = "";
  for (const piece of pieces) {
    pending += piece;
    const ending = pending.replace(CLOSE, "");
    const token =
      ending.match(/(?:^|[\s"'“‘([{\u300C\u300E])([A-Za-z.]+\.)$/u)?.[1] || "";
    // Segmenter can report "Dr." or "J." as a sentence; retain the name.
    if (
      ABBREVIATIONS.test(token) ||
      /^(?:[A-Za-z]\.)+$/u.test(token) ||
      /^\s*\d+[.)]\s*$/u.test(pending)
    )
      continue;
    result.push(pending);
    pending = "";
  }
  if (pending) result.push(pending);
  return result;
}

function wordBoundaries(text, locale) {
  const native = segments(text, locale, "word");
  if (!native) {
    // Without a dictionary, whitespace is safer than guessing Chinese words.
    return Array.from(
      text.matchAll(/\s+/gu),
      (match) => match.index + match[0].length,
    ).filter((end) => safeGraphemeBoundary(text, end));
  }

  // Preserve product codes, decimals, email addresses and emoji sequences.
  // Han words use the locale's word boundaries.
  const protectedRuns = Array.from(
    text.matchAll(
      /[^\s\p{Script=Han}，。！？；：、（）「」『』《》〈〉【】]+/gu,
    ),
    (match) => [match.index, match.index + match[0].length],
  );
  let run = 0;
  return native
    .map(({ index, segment }) => index + segment.length)
    .filter((end) => {
      while (run < protectedRuns.length && protectedRuns[run][1] <= end)
        run += 1;
      if (run < protectedRuns.length && protectedRuns[run][0] < end)
        return false;
      // Keep closing punctuation with the preceding word, opening punctuation
      // with the following word.
      return (
        !/^[,.;:!?。，；：！？、"'”’»」』）〉》】)\]}]/u.test(
          text.slice(end),
        ) && !/[([{"“‘「『（〈《【]$/u.test(text.slice(0, end))
      );
    });
}

function lastBoundary(boundaries, start, limit) {
  let low = 0;
  let high = boundaries.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (boundaries[mid] <= limit) low = mid + 1;
    else high = mid;
  }
  return low > 0 && boundaries[low - 1] > start ? boundaries[low - 1] : null;
}

// edge-tts splits again above 4096 bytes after XML escaping. Stay below that
// ceiling too, so it cannot undo our sentence/word boundaries for Chinese.
function requestEnd(text, start, maxChars) {
  let end = start;
  let bytes = 0;
  while (end < text.length) {
    const point = text.codePointAt(end);
    const width = point > 0xffff ? 2 : 1;
    const size =
      point === 38
        ? 5
        : point === 60 || point === 62
          ? 4
          : point <= 0x7f
            ? 1
            : point <= 0x7ff
              ? 2
              : point <= 0xffff
                ? 3
                : 4;
    if (end + width - start > maxChars || bytes + size > 4096) break;
    end += width;
    bytes += size;
  }
  return end;
}

function fitSentence(sentence, locale, maxChars) {
  if (requestEnd(sentence, 0, maxChars) === sentence.length) return [sentence];
  const clauses = punctuationBoundaries(sentence, CLAUSE_END);
  let words;
  const pieces = [];
  let start = 0;
  while (start < sentence.length) {
    const limit = requestEnd(sentence, start, maxChars);
    if (limit === sentence.length) break;
    let end = lastBoundary(clauses, start, limit);
    if (end === null) {
      if (!words) words = wordBoundaries(sentence, locale);
      end = lastBoundary(words, start, limit);
    }
    if (end === null) {
      throw new Error(
        "This passage is too long to read without cutting a word. " +
          "Try reading a shorter passage with sentence breaks.",
      );
    }
    pieces.push(sentence.slice(start, end));
    start = end;
  }
  if (start < sentence.length) pieces.push(sentence.slice(start));
  return pieces;
}

// Chunk targets in characters, sized for similar listening time per language.
// Chinese is read at ~4–5 characters a second against ~15 for English, so the
// same count would be 3× the audio: a longer wait before the first clip (the
// backend synthesizes a whole chunk before replying) and 3× the decoded buffer.
// First chunk ~16 s, the rest ~50 s.
const TARGETS = {
  en: { firstTarget: 240, target: 500 },
  zh: { firstTarget: 80, target: 100 },
};

/**
 * Preserve the complete prepared speech, including whitespace.
 * Targets are soft: a whole sentence may exceed its target, but not maxChars.
 * Only sentences above the hard ceiling fall back to clause/word boundaries.
 */
export function splitSpeechText(text, language = "en", options = {}) {
  const zh = /^zh/i.test(language);
  const defaults = zh ? TARGETS.zh : TARGETS.en;
  const {
    firstTarget = defaults.firstTarget,
    target = defaults.target,
    maxChars = MAX_TTS_CHARS,
  } = options;
  if (
    ![firstTarget, target, maxChars].every(
      (value) => Number.isInteger(value) && value > 0,
    ) ||
    maxChars > MAX_TTS_CHARS
  ) {
    throw new RangeError(
      "Speech chunk sizes must be positive integers; maxChars cannot exceed 2000.",
    );
  }
  if (!text || !text.trim()) return [];
  const locale = zh ? "zh-TW" : "en";
  const chunks = [];
  let current = "";
  for (const sentence of sentenceUnits(text, locale)) {
    for (const unit of fitSentence(sentence, locale, maxChars)) {
      const limit = Math.min(
        maxChars,
        chunks.length === 0 ? firstTarget : target,
      );
      if (
        current &&
        (current.length + unit.length > limit ||
          requestEnd(current + unit, 0, maxChars) <
            current.length + unit.length)
      ) {
        chunks.push(current);
        current = "";
      }
      current += unit;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}
